import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, GitBranch, Gavel, X } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { DataTable } from "@/components/common/DataTable";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RowActions } from "./RowActions";
import { Button } from "@/components/ui/button";
import { useCrud } from "@/hooks/useCrud";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { decideFields } from "@/components/dr/drFields";
import { OWNERS, PRIORITY_LEVELS, DECISION_STATUS, priorityBadge, statusBadge } from "@/data/commandOptions";

const today = () => new Date().toISOString().slice(0, 10);

const FIELDS = [
  { name: "title", label: "Decision title", type: "text", placeholder: "e.g. Launch platform first", required: true },
  { name: "context", label: "Context", type: "textarea", placeholder: "Background to this decision" },
  { name: "options", label: "Options being considered", type: "textarea", placeholder: "Option A vs Option B…" },
  { name: "owner", label: "Owner", type: "select", placeholder: "Owner", options: OWNERS, required: true },
  { name: "priority", label: "Priority", type: "select", placeholder: "Priority", options: PRIORITY_LEVELS, required: true },
  { name: "deadline", label: "Decision deadline", type: "date" },
  { name: "status", label: "Status", type: "select", placeholder: "Status", options: DECISION_STATUS, required: true },
  { name: "final_decision", label: "Final decision", type: "textarea", placeholder: "What was decided" },
  { name: "reason", label: "Reason", type: "textarea", placeholder: "Why this call" },
];

export const DecisionsPanel = ({ filter }) => {
  const { items, save, remove, refetch } = useCrud("decisions", "Decision");
  const { currentUser, canWrite } = useUser();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [decideRow, setDecideRow] = useState(null);

  const awaitingOnly = filter === "awaiting";
  const rows = awaitingOnly ? items.filter((d) => d.status !== "Decided") : items;

  const decide = async (v) => {
    await api.create(`decisions/${decideRow.id}/transition`, { new_status: "Decided", final_decision: v.final_decision, reason: v.reason, author: currentUser.name });
    toast.success("Decision recorded");
    await refetch();
    setDecideRow(null);
  };

  const openAdd = () => { setEditing({ owner: "Steven", priority: "Medium", status: "Awaiting decision" }); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  const columns = [
    { key: "title", header: "Decision", render: (r) => (
      <div>
        <p className="font-medium text-[#0D1B2A]">{r.title}</p>
        {r.deadline && <p className={`mt-0.5 text-xs ${r.status !== "Decided" && r.deadline < today() ? "font-semibold text-[#9B2226]" : "text-[#5C6B7B]"}`}>Deadline {r.deadline}</p>}
      </div>
    )},
    { key: "owner", header: "Owner" },
    { key: "priority", header: "Priority", render: (r) => <StatusBadge status={priorityBadge(r.priority)} label={r.priority} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={statusBadge(r.status)} label={r.status} /> },
    { key: "actions", header: "", thClassName: "text-right", className: "text-right", render: (r) => (
      <RowActions idx={r.id} onEdit={() => openEdit(r)} onDelete={() => setConfirmId(r.id)}
        extra={r.status !== "Decided" && (
          <Button size="sm" variant="ghost" data-testid={`decide-${r.id}`}
            onClick={() => setDecideRow(r)}
            className="h-8 gap-1 text-[#2D6A4F] hover:bg-[#2D6A4F]/10">
            <Gavel size={15} /> Decided
          </Button>
        )}
      />
    )},
  ];

  return (
    <SectionCard
      testid="decisions-card"
      accent="#E0A82E"
      eyebrow="Awaiting Command Approval"
      icon={GitBranch}
      title="Decisions Requiring Attention"
      subtitle="Decisions awaiting a call — those approaching or past deadline surface on the overview."
      action={canWrite ? <Button size="sm" data-testid="add-decision-btn" onClick={openAdd} className="rounded-full bg-[#E0A82E] text-white hover:bg-[#C79422]"><Plus size={15} /> Add</Button> : null}
    >
      {awaitingOnly && (
        <div data-testid="decisions-filter-banner" className="mb-3 flex items-center justify-between rounded-xl bg-[#E0A82E]/10 px-3 py-2 text-xs font-semibold text-[#B45309]">
          <span>Filtered to decisions awaiting a call</span>
          <button data-testid="clear-filter" onClick={() => navigate("/?tab=decisions")} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear</button>
        </div>
      )}
      <DataTable
        testid="decisions-table"
        columns={columns}
        rows={rows}
        searchKeys={["title", "owner", "context"]}
        searchPlaceholder="Search decisions…"
        filters={[
          { key: "owner", label: "Owner", options: OWNERS },
          { key: "priority", label: "Priority", options: PRIORITY_LEVELS },
          { key: "status", label: "Status", options: DECISION_STATUS },
        ]}
        emptyIcon={GitBranch}
        emptyImage={awaitingOnly ? undefined : "/commando-mascot-v2.webp"}
        emptyTitle={awaitingOnly ? "No decisions awaiting" : "No decisions logged"}
        emptyDescription={awaitingOnly ? "Every decision has been made — nothing pending." : "Capture decisions that need a call so nothing stalls."}
        emptyActionLabel={awaitingOnly || !canWrite ? undefined : "Add decision"}
        onEmptyAction={awaitingOnly || !canWrite ? undefined : openAdd}
      />
      <EntryModal open={modalOpen} onOpenChange={setModalOpen}
        title={editing?.id ? "Edit Decision" : "Add Decision"}
        submitLabel={editing?.id ? "Save Changes" : "Add Decision"}
        fields={FIELDS} initialValues={editing} onSubmit={save} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete this decision?"
        onConfirm={(reason) => { remove(confirmId, reason); setConfirmId(null); }} softDelete />
      <EntryModal open={!!decideRow} onOpenChange={(o) => !o && setDecideRow(null)}
        title="Record Decision" description="A final decision and reason are required to mark this decided."
        submitLabel="Mark Decided" fields={decideFields()} initialValues={{}} onSubmit={decide} />
    </SectionCard>
  );
};
